import { useToast } from "../../hooks/use-toast";
import { TOKEN_KEY } from "./constants";
import { useAuthActions } from "./useAuthActions";
import { useClientActions } from "./useClientActions";
import { useDashboardData } from "./useDashboardData";
import { useDeveloperActions } from "./useDeveloperActions";
import { useSupportPortalState } from "./useSupportPortalState";
import { useTicketActions } from "./useTicketActions";

export function useSupportPortal() {
  const { toast } = useToast();
  const state = useSupportPortalState();
  const {
    activeDevelopers,
    activeView,
    adminClientForm,
    adminClients,
    adminComment,
    adminDeveloperForm,
    adminDevelopers,
    adminStatus,
    authHeaders,
    clientSearch,
    clientStatusFilter,
    developmentTimeMinutes,
    filteredAdminClients,
    filteredTickets,
    forgotPasswordForm,
    isDeleteClientDialogOpen,
    isDeleteTicketDialogOpen,
    isDeveloperEditorOpen,
    isEditingClient,
    isEditingDeveloper,
    isResetPasswordFormValid,
    isTmaPackageDialogOpen,
    kanbanColumns,
    loading,
    loadingData,
    loginForm,
    messageDraft,
    messageFiles,
    mode,
    notifications,
    resetPasswordForm,
    selectedAdminClient,
    selectedClientId,
    selectedTicket,
    selectedTicketId,
    selectedTicketIdRef,
    setActiveView,
    setAdminClientForm,
    setAdminClients,
    setAdminComment,
    setAdminDeveloperForm,
    setAdminDevelopers,
    setAdminStatus,
    setClientSearch,
    setClientStatusFilter,
    setDevelopmentTimeMinutes,
    setForgotPasswordForm,
    setIsClientEditorOpen,
    setIsDeleteClientDialogOpen,
    setIsDeleteTicketDialogOpen,
    setIsDeveloperEditorOpen,
    setIsTmaPackageDialogOpen,
    setLoading,
    setLoadingData,
    setLoginForm,
    setMessageDraft,
    setMessageFiles,
    setMode,
    setNotifications,
    setResetPasswordForm,
    setSelectedClientId,
    setSelectedTicket,
    setShowAdminClientPassword,
    setShowAdminDeveloperPassword,
    setShowLoginPassword,
    setShowResetPassword,
    setShowResetPasswordConfirm,
    setTicketDisplayMode,
    setTicketFiles,
    setTicketForm,
    setTickets,
    setTmaPackageForm,
    setToken,
    setUnreadCount,
    setUser,
    showAdminClientPassword,
    showAdminDeveloperPassword,
    showLoginPassword,
    showResetPassword,
    showResetPasswordConfirm,
    ticketDisplayMode,
    ticketFiles,
    ticketForm,
    tickets,
    tmaPackageForm,
    token,
    unreadCount,
    updateSelectedTicketId,
    user,
  } = state;

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setUser(null);
    setTickets([]);
    setAdminClients([]);
    setAdminDevelopers([]);
    updateSelectedTicketId("");
    setSelectedTicket(null);
    setSelectedClientId("");
    setNotifications([]);
    setUnreadCount(0);
    setActiveView("tickets");
  };

  const { fetchDashboard } = useDashboardData({
    handleLogout,
    selectedTicketIdRef,
    setAdminClients,
    setAdminDevelopers,
    setAdminStatus,
    setDevelopmentTimeMinutes,
    setLoadingData,
    setNotifications,
    setSelectedClientId,
    setSelectedTicket,
    setTickets,
    setUnreadCount,
    setUser,
    token,
    updateSelectedTicketId,
  });

  const { handleForgotPassword, handleLogin, handleResetPassword } = useAuthActions({
    fetchDashboard,
    forgotPasswordForm,
    loginForm,
    resetPasswordForm,
    setActiveView,
    setAdminClients,
    setAdminDevelopers,
    setForgotPasswordForm,
    setLoading,
    setLoginForm,
    setMode,
    setNotifications,
    setResetPasswordForm,
    setSelectedClientId,
    setSelectedTicket,
    setTickets,
    setToken,
    setUnreadCount,
    setUser,
    toast,
    updateSelectedTicketId,
  });

  const {
    handleAddTmaPackage,
    handleCreateClient,
    handleDeleteClient,
    handleEditClient,
    openCreateClientForm,
    openTmaPackageDialog,
    resetClientForm,
  } = useClientActions({
    adminClientForm,
    authHeaders,
    fetchDashboard,
    isEditingClient,
    selectedAdminClient,
    setAdminClientForm,
    setIsClientEditorOpen,
    setIsDeleteClientDialogOpen,
    setIsTmaPackageDialogOpen,
    setLoading,
    setSelectedClientId,
    setSelectedTicket,
    setTmaPackageForm,
    tmaPackageForm,
    toast,
    updateSelectedTicketId,
  });

  const {
    handleCreateDeveloper,
    handleDeleteDeveloper,
    handleEditDeveloper,
    openCreateDeveloperForm,
    resetDeveloperForm,
  } = useDeveloperActions({
    adminDeveloperForm,
    authHeaders,
    fetchDashboard,
    isEditingDeveloper,
    setAdminDeveloperForm,
    setIsDeveloperEditorOpen,
    setLoading,
    toast,
  });

  const {
    downloadAttachment,
    handleCreateTicket,
    handleDeleteTicket,
    handleMessageFilesChange,
    handleSendMessage,
    handleStatusUpdate,
    handleTicketAssignmentChange,
    handleTicketFilesChange,
    markAsRead,
    openTicket,
  } = useTicketActions({
    adminComment,
    adminStatus,
    authHeaders,
    developmentTimeMinutes,
    fetchDashboard,
    messageDraft,
    messageFiles,
    selectedTicket,
    setActiveView,
    setAdminComment,
    setAdminStatus,
    setDevelopmentTimeMinutes,
    setIsDeleteTicketDialogOpen,
    setLoading,
    setMessageDraft,
    setMessageFiles,
    setSelectedTicket,
    setTicketFiles,
    setTicketForm,
    ticketFiles,
    ticketForm,
    toast,
    token,
    selectedTicketIdRef,
    updateSelectedTicketId,
  });

  return {
    auth: {
      forgotPasswordForm,
      handleForgotPassword,
      handleLogin,
      handleResetPassword,
      isResetPasswordFormValid,
      loginForm,
      mode,
      resetPasswordForm,
      setForgotPasswordForm,
      setLoginForm,
      setMode,
      setResetPasswordForm,
      setShowLoginPassword,
      setShowResetPassword,
      setShowResetPasswordConfirm,
      showLoginPassword,
      showResetPassword,
      showResetPasswordConfirm,
    },
    clients: {
      adminClientForm,
      adminClients,
      clientSearch,
      clientStatusFilter,
      filteredAdminClients,
      handleAddTmaPackage,
      handleCreateClient,
      handleDeleteClient,
      handleEditClient,
      isDeleteClientDialogOpen,
      isEditingClient,
      isTmaPackageDialogOpen,
      openCreateClientForm,
      openTmaPackageDialog,
      resetClientForm,
      selectedAdminClient,
      selectedClientId,
      setAdminClientForm,
      setClientSearch,
      setClientStatusFilter,
      setIsDeleteClientDialogOpen,
      setIsTmaPackageDialogOpen,
      setSelectedClientId,
      setShowAdminClientPassword,
      setTmaPackageForm,
      showAdminClientPassword,
      tmaPackageForm,
    },
    developers: {
      activeDevelopers,
      adminDeveloperForm,
      adminDevelopers,
      handleCreateDeveloper,
      handleDeleteDeveloper,
      handleEditDeveloper,
      isDeveloperEditorOpen,
      isEditingDeveloper,
      openCreateDeveloperForm,
      resetDeveloperForm,
      setAdminDeveloperForm,
      setShowAdminDeveloperPassword,
      showAdminDeveloperPassword,
    },
    navigation: {
      activeView,
      setActiveView,
    },
    notifications: {
      markAsRead,
      notifications,
      unreadCount,
    },
    session: {
      handleLogout,
      user,
    },
    tickets: {
      adminComment,
      adminStatus,
      developmentTimeMinutes,
      downloadAttachment,
      filteredTickets,
      handleCreateTicket,
      handleDeleteTicket,
      handleSendMessage,
      handleMessageFilesChange,
      handleStatusUpdate,
      handleTicketAssignmentChange,
      handleTicketFilesChange,
      isDeleteTicketDialogOpen,
      kanbanColumns,
      messageDraft,
      messageFiles,
      openTicket,
      selectedTicket,
      selectedTicketId,
      setAdminComment,
      setAdminStatus,
      setDevelopmentTimeMinutes,
      setIsDeleteTicketDialogOpen,
      setMessageDraft,
      setTicketDisplayMode,
      setTicketForm,
      ticketDisplayMode,
      ticketForm,
      tickets,
    },
    ui: {
      loading,
      loadingData,
    },
  };
}
